import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { UserService } from '../services/user/user.service';
import { map, switchMap } from 'rxjs/operators';
import { from } from 'rxjs';
import { RolesEnum } from '../models/enums/roles.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BiometricService } from '../services/biometric/biometric.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private router: Router,
    private userService: UserService,
    private biometricService: BiometricService
  ) {}

  canActivate(route: ActivatedRouteSnapshot) {
    const allowedRoles: RolesEnum[] = route.data['allowedRoles'];

    return from(this.biometricService.biometricIsAvailable()).pipe(
      switchMap(isBiometricAvailable =>
        this.userService.onlineUser$.pipe(
          map(user => {
            const hasUser: boolean = !!user;
            const hasToken: boolean = !!this.userService.token;
            const hasAllowedRole: boolean = !!allowedRoles.find(role => role === user?.role.name);

            if (hasUser && hasToken && hasAllowedRole) {
              return true;
            }

            if (hasUser && hasToken && !hasAllowedRole) {
              this.router.navigate(['/auth']);
              return false;
            }

            if (hasUser && !hasToken && hasAllowedRole && isBiometricAvailable) {
              this.router.navigate(['/biometric-auth']);
              return false;
            }

            if (hasUser && !hasToken && hasAllowedRole && !isBiometricAvailable) {
              this.router.navigate(['/auth']);
              return false;
            }

            if (hasUser && !hasToken && !hasAllowedRole) {
              this.router.navigate(['/auth']);
              return false;
            }

            if (!hasUser) {
              this.router.navigate(['/auth']);
              return false;
            }

            this.router.navigate(['/auth']);
            return false;
          })
        )
      ),
      untilDestroyed(this)
    );
  }
}

export const canActivate: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return inject(AuthGuard).canActivate(route);
};
